import React from 'react'
import clsx from 'clsx'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import { Button } from '@wartek-id/button-v1'
import { Chip } from '@wartek-id/chip'
import { Icon } from '@wartek-id/icon-v2'
import {
  SelectionGroup,
  SelectionGroupOption,
} from '@wartek-id/selection-group'
import {
  Sheet,
  SheetCloseButton,
  SheetContent,
  SheetOverlay,
  SheetTitle,
} from '@wartek-id/sheet-v1'
import { Text } from '@wartek-id/text'
import { Textarea } from '@wartek-id/textarea'
import { useToast } from '@wartek-id/toast'

import { useConversationStore } from 'stores/conversation'
import { LIKE_DISLIKE_CONTENTS } from 'app/ConversationPage/constants'
import { useSETrackEvent } from 'app/ConversationPage/hooks/useSETrackEvent'

interface IMessageFeedbackActionSheetProps {
  isOpen: boolean
  message: string
  mode?: 'client' | 'server'
  onClose: () => void
  onCopy: () => void
  onLike: () => void
  onDislike: () => void
}
export const MessageFeedbackActionSheet: React.FC<
  IMessageFeedbackActionSheetProps
> = ({ isOpen, message, mode, onClose, onCopy, onLike, onDislike }) => {
  const isServer = React.useMemo(() => mode === 'server', [mode])

  return (
    <Sheet
      autoFocus={false}
      isOpen={isOpen}
      closeOnOverlayClick
      onClose={onClose}
    >
      <SheetOverlay />
      <SheetContent className="max-w-xl m-auto">
        <div className="px-4 py-2">
          <div className="py-4">
            <CopyToClipboard
              text={message}
              onCopy={() => {
                onCopy()
                onClose()
              }}
            >
              <button
                data-testid="conversation-message-option-copy"
                aria-label="Salin"
                className="flex w-full space-x-2"
              >
                <Icon>content_copy</Icon>
                <Text variant="heading-sm">Salin {isServer && 'jawaban'}</Text>
              </button>
            </CopyToClipboard>
          </div>

          {isServer && (
            <>
              <div className="py-4">
                <button
                  data-testid="conversation-message-option-like"
                  aria-label="Menyukai"
                  className="flex w-full space-x-2"
                  onClick={onLike}
                >
                  <Icon>thumb_up_outline</Icon>
                  <Text variant="heading-sm">Saya suka jawaban ini</Text>
                </button>
              </div>

              <div className="py-4">
                <button
                  data-testid="conversation-message-option-dislike"
                  aria-label="Tidak Menyukai"
                  className="flex w-full space-x-2"
                  onClick={onDislike}
                >
                  <Icon>thumb_down_outline</Icon>
                  <Text variant="heading-sm">Tidak suka jawaban ini</Text>
                </button>
              </div>
            </>
          )}

          <div className="py-4">
            <Button color="white" fullWidth onClick={onClose}>
              Tutup
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}

interface IMessageLikeDislikeActionSheetProps {
  messageId?: string
  isOpen: boolean
  action?: string
  onClose: () => void
}
export const MessageLikeDislikeActionSheet: React.FC<
  IMessageLikeDislikeActionSheetProps
> = ({ messageId, isOpen, action, onClose }) => {
  const toast = useToast()
  const { title, caption, options, toastMessage } = React.useMemo(
    () => LIKE_DISLIKE_CONTENTS[action || 'like'],
    [action]
  )
  const methods = useForm({
    mode: 'onChange',
    defaultValues: {
      reasons: [],
      reasonDetail: '',
    },
  })
  const {
    control,
    formState: { isValid },
    handleSubmit,
    register,
    reset,
  } = methods
  const { conversationId } = useConversationStore()
  const { SETrackConversationPage } = useSETrackEvent()

  const submitHandler = (data) => {
    SETrackConversationPage('CLICK_GURU_AI_REVIEW', {
      ai_chat_session_id: conversationId,
      ai_message_id: messageId,
      page_element: action.toUpperCase(),
      tags: (data?.reasons ?? []).join(' | '),
      text_input: data?.reasonDetail ?? '',
    })
    toast({
      message: toastMessage,
      type: 'success',
    })
    onClose()
  }

  React.useEffect(() => {
    reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  return (
    <Sheet
      autoFocus={false}
      isOpen={isOpen}
      closeOnOverlayClick
      onClose={onClose}
    >
      <SheetOverlay />
      <SheetContent className="max-w-xl m-auto">
        <SheetCloseButton />
        <SheetTitle>
          <Text variant="heading-md">{title}</Text>
        </SheetTitle>

        <div className="px-4 py-2">
          <div className="mb-2">
            <Text>{caption}</Text>
          </div>

          <FormProvider {...methods}>
            <div className="mb-6">
              <Controller
                control={control}
                name="reasons"
                rules={{
                  required: true,
                }}
                render={({ field: { onChange, value } }) => (
                  <SelectionGroup
                    className="flex flex-row flex-wrap gap-2"
                    value={value}
                    onChange={onChange}
                  >
                    {options.map((option, index) => {
                      const isActive = value.includes(option)
                      return (
                        <SelectionGroupOption key={index} value={option}>
                          <Chip
                            data-testid="conversation-message-feedback-option-item"
                            isActive={isActive}
                            className={clsx(
                              isActive
                                ? '!border-interactive-default !bg-blue-5'
                                : '!border-default bg-white',
                              'focus:!ring-0'
                            )}
                          >
                            <Text
                              className={clsx(
                                isActive ? '!text-blue-70' : '!text-default'
                              )}
                              variant="action"
                            >
                              {option}
                            </Text>
                          </Chip>
                        </SelectionGroupOption>
                      )
                    })}
                  </SelectionGroup>
                )}
              />
            </div>

            <div className="mb-4 space-y-1">
              <Text>
                Berikan alasan detail{' '}
                <span className="text-subdued">(Opsional)</span>
              </Text>
              <Textarea
                data-testid="conversation-message-feedback-input"
                aria-label="Feedback Message"
                {...register('reasonDetail')}
                placeholder="Ketik alasan di sini"
              />
            </div>

            <div className="py-4">
              <Button
                data-testid="conversation-message-feedback-send-button"
                color="black"
                fullWidth
                disabled={!isValid}
                onClick={handleSubmit(submitHandler)}
              >
                Kirim
              </Button>
            </div>
          </FormProvider>
        </div>
      </SheetContent>
    </Sheet>
  )
}
